import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, ChevronLeft, ChevronRight, Settings, History, Calendar as CalendarIcon, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ConfiguracionGlobalModal from '../components/ConfiguracionGlobalModal';
import ConfiguracionDiaModal from '../components/ConfiguracionDiaModal';
import HistorialConfiguracionModal from '../components/HistorialConfiguracionModal';
import { parsearFechaCaracas } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const GestorCitas = () => {
  const navigate = useNavigate();
  const [mesActual, setMesActual] = useState(new Date());
  const [datosCitas, setDatosCitas] = useState(null);
  const [configGlobal, setConfigGlobal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [operadorFilter, setOperadorFilter] = useState(null);
  const [operadores, setOperadores] = useState([]);
  
  // Modales
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDiaModal, setShowDiaModal] = useState(false);
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);

  useEffect(() => {
    fetchConfigGlobal();
    fetchDatosCitas();
    fetchOperadores();
  }, [mesActual, operadorFilter]);

  const fetchConfigGlobal = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gestor-citas/configuracion-global`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConfigGlobal(data);
      }
    } catch (error) {
      console.error('Error al cargar configuración global:', error);
    }
  };

  const fetchDatosCitas = async () => {
    setLoading(true);
    try {
      const year = mesActual.getFullYear();
      const month = mesActual.getMonth() + 1;
      
      let url = `${BACKEND_URL}/api/gestor-citas/mes/${year}/${month}`;
      if (operadorFilter) {
        url += `?operador_id=${operadorFilter}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setDatosCitas(data);
      }
    } catch (error) {
      console.error('Error al cargar datos de citas:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperadores = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/usuarios/operadores`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOperadores(data.operadores || []);
      }
    } catch (error) {
      console.error('Error al cargar operadores:', error);
    }
  };

  const handleMesAnterior = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1, 1));
  };

  const handleMesSiguiente = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 1));
  };

  const handleDiaClick = (dia) => {
    setDiaSeleccionado(dia);
    setShowDiaModal(true);
  };

  const renderCalendario = () => {
    if (!datosCitas || !datosCitas.dias) return null;

    const primerDiaMes = new Date(mesActual.getFullYear(), mesActual.getMonth(), 1);
    const primerDiaSemana = primerDiaMes.getDay();
    const diasEnMes = datosCitas.dias.length;

    const calendario = [];
    let semana = [];

    // Espacios en blanco antes del primer día
    for (let i = 0; i < primerDiaSemana; i++) {
      semana.push(<div key={`empty-${i}`} className="h-24 border border-gray-800"></div>);
    }

    // Días del mes
    datosCitas.dias.forEach((dia, index) => {
      // Fix: Usar helper para parsear fecha correctamente en zona horaria de Caracas
      const fecha = parsearFechaCaracas(dia.fecha);
      const esHoy = new Date().toDateString() === fecha.toDateString();
      
      let bgColor = 'bg-slate-900';
      let borderColor = 'border-gray-700';
      let textColor = 'text-white';
      
      if (!dia.operativo) {
        bgColor = 'bg-red-900/20';
        borderColor = 'border-red-700';
        textColor = 'text-red-400';
      } else if (dia.cupos_disponibles === 0) {
        bgColor = 'bg-yellow-900/20';
        borderColor = 'border-yellow-700';
      } else if (dia.citas_agendadas > 0) {
        bgColor = 'bg-green-900/20';
        borderColor = 'border-green-700';
      }

      if (esHoy) {
        borderColor = 'border-yellow-500 border-2';
      }

      semana.push(
        <div
          key={dia.fecha}
          className={`h-24 border ${borderColor} ${bgColor} p-2 cursor-pointer hover:opacity-80 transition-opacity`}
          onClick={() => handleDiaClick(dia)}
        >
          <div className="flex justify-between items-start mb-1">
            <span className={`text-sm font-semibold ${textColor}`}>{fecha.getDate()}</span>
            {!dia.operativo && <AlertCircle className="w-4 h-4 text-red-400" />}
          </div>
          
          {dia.operativo ? (
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-400">Ocupadas:</span>
                <span className="text-white font-semibold">{dia.citas_agendadas}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Total:</span>
                <span className="text-white">{dia.cupos_totales}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Libres:</span>
                <span className={dia.cupos_disponibles > 0 ? 'text-green-400' : 'text-red-400'}>
                  {dia.cupos_disponibles}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-red-400 mt-2">
              {dia.motivo || 'No operativo'}
            </div>
          )}
        </div>
      );

      if (semana.length === 7) {
        calendario.push(
          <div key={`week-${index}`} className="grid grid-cols-7 gap-1">
            {semana}
          </div>
        );
        semana = [];
      }
    });

    // Completar última semana si es necesaria
    if (semana.length > 0) {
      while (semana.length < 7) {
        semana.push(<div key={`empty-end-${semana.length}`} className="h-24 border border-gray-800"></div>);
      }
      calendario.push(
        <div key="week-last" className="grid grid-cols-7 gap-1">
          {semana}
        </div>
      );
    }

    return calendario;
  };

  if (loading && !datosCitas) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando gestor de citas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mb-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-yellow-500 mb-2">
                📅 Gestor de Citas
              </h1>
              <p className="text-gray-400">
                Administra la disponibilidad y configuración de citas
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowConfigModal(true)}
                variant="outline"
                className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
              >
                <Settings className="w-4 h-4 mr-2" />
                Config. Global
              </Button>
              <Button
                onClick={() => setShowHistorialModal(true)}
                variant="outline"
                className="border-gray-700 text-gray-400 hover:bg-gray-700"
              >
                <History className="w-4 h-4 mr-2" />
                Historial
              </Button>
            </div>
          </div>
        </div>

        {/* Filtros y Navegación del Mes */}
        <Card className="bg-slate-900 border-gray-700 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleMesAnterior}
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                
                <h2 className="text-xl font-semibold text-white min-w-[200px] text-center">
                  {MESES[mesActual.getMonth()]} {mesActual.getFullYear()}
                </h2>
                
                <Button
                  onClick={handleMesSiguiente}
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {operadores.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Filtrar por operador:</span>
                  <select
                    value={operadorFilter || ''}
                    onChange={(e) => setOperadorFilter(e.target.value || null)}
                    className="bg-slate-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="">Todos</option>
                    {operadores.map((op) => (
                      <option key={op.id} value={op.id}>
                        {op.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leyenda */}
        <div className="flex flex-wrap gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-slate-900 border border-gray-700"></div>
            <span className="text-gray-400">Sin citas</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-900/20 border border-green-700"></div>
            <span className="text-gray-400">Con citas agendadas</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-900/20 border border-yellow-700"></div>
            <span className="text-gray-400">Cupo lleno</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-900/20 border border-red-700"></div>
            <span className="text-gray-400">No operativo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-yellow-500"></div>
            <span className="text-gray-400">Hoy</span>
          </div>
        </div>

        {/* Calendario */}
        <Card className="bg-slate-900 border-gray-700">
          <CardHeader>
            <div className="grid grid-cols-7 gap-1 text-center">
              {DIAS_SEMANA.map((dia) => (
                <div key={dia} className="text-sm font-semibold text-gray-400 py-2">
                  {dia}
                </div>
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            {renderCalendario()}
          </CardContent>
        </Card>
      </div>

      {/* Modales */}
      {showConfigModal && (
        <ConfiguracionGlobalModal
          configActual={configGlobal}
          onClose={() => setShowConfigModal(false)}
          onSave={() => {
            setShowConfigModal(false);
            fetchConfigGlobal();
            fetchDatosCitas();
          }}
        />
      )}

      {showDiaModal && diaSeleccionado && (
        <ConfiguracionDiaModal
          dia={diaSeleccionado}
          configGlobal={configGlobal}
          onClose={() => {
            setShowDiaModal(false);
            setDiaSeleccionado(null);
          }}
          onSave={() => {
            setShowDiaModal(false);
            setDiaSeleccionado(null);
            fetchDatosCitas();
          }}
        />
      )}

      {showHistorialModal && (
        <HistorialConfiguracionModal
          onClose={() => setShowHistorialModal(false)}
        />
      )}
    </div>
  );
};

export default GestorCitas;
